import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import cvPdf from '../assets/Abdulaziz_CV.pdf';
import { useTranslation } from 'react-i18next';

const fontStack = `'Fira Mono', 'Menlo', 'Consolas', 'Liberation Mono', 'monospace'`;

const TYPED_TEXT = 'python info.py';
const TYPING_SPEED = 80; // ms per character

const terminalLinkStyle = {
  color: '#e95420',
  textDecoration: 'underline',
  fontWeight: 700,
  fontFamily: 'Fira Mono, Menlo, Consolas, Liberation Mono, monospace',
  fontSize: '1.1em',
  margin: '0 6px',
  cursor: 'pointer',
};

const Hero = () => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [showContent, setShowContent] = useState(false);
  const [lsTyped, setLsTyped] = useState('');
  const [showLinks, setShowLinks] = useState(false);
  const { i18n } = useTranslation();
  const reduceMotion = useReducedMotion();

  const TERMINAL_OUTPUT = [
    { type: 'output', value: i18n.t('hero.output.name') },
    { type: 'output', value: i18n.t('hero.output.role') },
    { type: 'output', value: i18n.t('hero.output.skills') },
    { type: 'output', value: i18n.t('hero.output.portfolio') },
  ];
  
  const SECTION_LINKS = [
    { label: i18n.t('hero.links.about'), href: '#about' },
    { label: i18n.t('hero.links.projects'), href: '#projects' },
    { label: i18n.t('hero.links.skills'), href: '#skills' },
    { label: i18n.t('hero.links.contact'), href: '#contact' },
  ];

  useEffect(() => {
    setTimeout(() => setOpen(true), 400);
  }, []);

  useEffect(() => {
    if (open && typed.length < TYPED_TEXT.length) {
      const timeout = setTimeout(() => {
        setTyped(TYPED_TEXT.slice(0, typed.length + 1));
      }, TYPING_SPEED);
      return () => clearTimeout(timeout);
    } else if (typed.length === TYPED_TEXT.length) {
      setTimeout(() => setShowContent(true), 400);
    }
  }, [open, typed]);

  // Animate 'ls' command after info output
  useEffect(() => {
    if (showContent && lsTyped.length < 2) {
      const timeout = setTimeout(() => {
        setLsTyped('ls'.slice(0, lsTyped.length + 1));
      }, 120);
      return () => clearTimeout(timeout);
    } else if (lsTyped.length === 2) {
      setTimeout(() => setShowLinks(true), 350);
    }
  }, [showContent, lsTyped]);

  // Scroll to section on link click
  const handleSectionClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component={motion.div}
      initial={reduceMotion ? false : { opacity: 0, y: 36, scale: 0.96 }}
      animate={open ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 36, scale: 0.96 }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: 420, sm: 540 },
        width: '100%',
        mt: 6,
        mb: 8,
      }}
    >
        <Box sx={{
          width: { xs: '98vw', sm: 800, md: 1000, lg: 1200, xl: 1800, '2xl': 2000 },
          maxWidth: '90vw',
          borderRadius: 2.5,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          overflow: 'hidden',
          background: 'rgba(36, 12, 44, 0.98)',
          border: '1.5px solid #77216f',
          position: 'relative',
          zIndex: 2,
          transform: open ? 'scale(1)' : 'scale(0.85)',
          transition: 'transform 0.7s cubic-bezier(.4,2,.3,1)',
        }}>
          {/* Ubuntu Terminal Bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            height: 32,
            px: 2,
            background: 'linear-gradient(90deg, #77216f 60%, #e95420 100%)',
            borderBottom: '1px solid #77216f',
            position: 'relative',
          }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e95420', mr: 1, boxShadow: '0 0 2px #e95420' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f6c915', mr: 1, boxShadow: '0 0 2px #f6c915' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#25d366', boxShadow: '0 0 4px #25d366' }} />
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ color: '#fff', fontFamily: fontStack, fontSize: 14, opacity: 0.7, pr: 1 }}>
              bash — 80x24
            </Box>
          </Box>
          {/* Terminal content */}
          <Box sx={{
            fontFamily: fontStack,
            color: '#fff',
            fontSize: { xs: 18, sm: 22 },
            px: { xs: 2, sm: 4 },
            py: { xs: 4, sm: 6 },
            minHeight: 320,
            background: 'rgba(36, 12, 44, 0.98)',
            position: 'relative',
            zIndex: 2,
          }}>
            {/* Prompt and command */}
            <Box>
              <Box component="span" sx={{ color: '#25d366', fontWeight: 600 }}>{'abdulaziz@ubuntu'}</Box>
              <Box component="span" sx={{ color: '#61afef', ml: 1 }}>{'~/web-dev'}</Box>
              <Box component="span" sx={{ color: '#fff', ml: 1 }}>$</Box>
              <Box component="span" sx={{ color: '#fff', fontWeight: 600, ml: 1 }}>
                {typed}
                <Box component="span" sx={{
                  display: 'inline-block',
                  width: 10,
                  height: 28,
                  ml: 0.5,
                  bgcolor: '#fff',
                  animation: 'blink 1.1s steps(1) infinite',
                  verticalAlign: 'middle',
                  visibility: typed.length === TYPED_TEXT.length ? 'hidden' : 'visible',
                }} />
              </Box>
            </Box>
            {/* Simulated output after command is typed */}
            {showContent && <>
              <Box sx={{ mt: 2 }}>
                {TERMINAL_OUTPUT.map((line, idx) => (
                  <Box key={idx} sx={{ fontFamily: fontStack, color: '#fff', fontSize: { xs: 16, sm: 18 }, mb: 0.5 }}>
                    {line.value}
                  </Box>
                ))}
              </Box>
              {/* New prompt */}
              <Box sx={{ mt: 3 }}>
                <Box component="span" sx={{ color: '#25d366', fontWeight: 600 }}>{'abdulaziz@ubuntu'}</Box>
                <Box component="span" sx={{ color: '#61afef', ml: 1 }}>{'~/web-dev'}</Box>
                <Box component="span" sx={{ color: '#fff', ml: 1 }}>$</Box>
                <Box component="span" sx={{ color: '#fff', fontWeight: 600, ml: 1 }}>
                  {/* Animate 'ls' command */}
                  {lsTyped}
                  <Box component="span" sx={{
                    display: 'inline-block',
                    width: 10,
                    height: 28,
                    ml: 0.5,
                    bgcolor: '#fff',
                    animation: 'blink 1.1s steps(1) infinite',
                    verticalAlign: 'middle',
                    visibility: lsTyped.length === 2 ? 'hidden' : 'visible',
                  }} />
                </Box>
              </Box>
              {/* Section links as terminal output */}
              {showLinks && (
                <>
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    {SECTION_LINKS.map((link, idx) => (
                      <React.Fragment key={link.href}>
                        <a
                          href={link.href}
                          onClick={(e) => handleSectionClick(e, link.href)}
                          style={terminalLinkStyle}
                        >
                          {link.label}
                        </a>
                        {idx < SECTION_LINKS.length - 1 && (
                          <span style={{ color: '#fff', opacity: 0.7, fontWeight: 700, fontSize: '1.1em', userSelect: 'none' }}>|</span>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: { xs: 14, sm: 16 }, mr: 0.5 }}>
                      Abdulaziz_CV.pdf →
                    </Box>
                    <a
                      href={cvPdf}
                      download="Abdulaziz_Almuflehi_CV.pdf"
                      style={terminalLinkStyle}
                    >
                      {i18n.t('hero.cv.download')}
                    </a>
                    <span style={{ color: '#fff', opacity: 0.7, fontWeight: 700, fontSize: '1.1em', userSelect: 'none' }}>|</span>
                    <a
                      href={cvPdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={terminalLinkStyle}
                    >
                      {i18n.t('hero.cv.open')}
                    </a>
                  </Box>
                </>
              )}
            </>}
          </Box>
          <style>{`
            @keyframes blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0; }
            }
          `}</style>
        </Box>
    </Box>
  );
};

export default Hero;